import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import BibleReaderModal from "../components/BibleReaderModal";
import {
  Book,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Check,
  Square,
  CheckSquare,
  Calendar,
  Sparkles,
  Heart,
  Target,
  MessageCircle,
  ArrowLeft,
  List
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo do Ano da Contemplação 2026
const ContemplationLogo = ({ className = "" }) => (
  <div className={`flex flex-col items-center ${className}`}>
    <div className="relative">
      <span className="text-sm font-light tracking-[0.3em] text-white/90 absolute -top-1 left-0 border border-white/40 rounded-full px-3 py-0.5">
        2026
      </span>
      <div className="pt-5">
        <span className="text-white/80 text-sm tracking-wide ml-12">O ANO DA</span>
      </div>
    </div>
    <div className="relative">
      <span className="text-2xl md:text-3xl font-bold tracking-tight">
        <span className="text-orange-500">CONTEMPL</span>
        <span className="text-white">AÇÃO</span>
      </span>
      <div className="absolute bottom-0 left-0 right-1/3 h-1 bg-gradient-to-r from-orange-500/80 to-orange-500/20 rounded-full" />
    </div>
  </div>
);

export default function DevotionalPage() {
  const { user, currentBranch } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [progress, setProgress] = useState({ dias_lidos: [] });
  const [allProgress, setAllProgress] = useState({});
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bibleModalOpen, setBibleModalOpen] = useState(false);
  const [bibleVersion, setBibleVersion] = useState("nvt"); // NVT como padrão

  useEffect(() => {
    if (currentBranch) {
      loadPlans();
    } else {
      // If no branch selected, stop loading
      setLoading(false);
    }
  }, [currentBranch]);

  const loadPlans = async () => {
    setLoading(true);
    try {
      // Load all plans for current branch
      const plansRes = await axios.get(`${API}/devotional-plans`, {
        params: { filial_id: currentBranch?.id }
      });
      
      if (plansRes.data && plansRes.data.length > 0) {
        // Sort by created_at descending (most recent first)
        const sortedPlans = plansRes.data.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });
        
        setPlans(sortedPlans);
        
        // Load progress for all plans in parallel (faster)
        const progressPromises = sortedPlans.map(async (plan) => {
          try {
            const progressRes = await axios.get(`${API}/devotional-progress/${plan.id}`);
            return { planId: plan.id, progress: progressRes.data || { dias_lidos: [] } };
          } catch (e) {
            return { planId: plan.id, progress: { dias_lidos: [] } };
          }
        });
        
        const progressResults = await Promise.all(progressPromises);
        const progressMap = {};
        progressResults.forEach(({ planId, progress }) => {
          progressMap[planId] = progress;
        });
        setAllProgress(progressMap);
      } else {
        setPlans([]);
        setAllProgress({});
      }
    } catch (error) {
      console.error("Error loading devotional plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
    const planProgress = allProgress[plan.id] || { dias_lidos: [] };
    setProgress(planProgress);
    
    // Set current day to first unread day or first day
    const unreadIndex = plan.dias?.findIndex(
      (d, i) => !planProgress.dias_lidos?.includes(d.dia)
    );
    setCurrentDayIndex(unreadIndex >= 0 ? unreadIndex : 0);
  };

  const goBackToList = () => {
    setSelectedPlan(null);
    setProgress({ dias_lidos: [] });
    setCurrentDayIndex(0);
  };

  const toggleDay = async (dayNumber) => {
    try {
      const response = await axios.post(`${API}/devotional-progress/${selectedPlan.id}/toggle-day/${dayNumber}`);
      setProgress(response.data);
      setAllProgress(prev => ({
        ...prev,
        [selectedPlan.id]: response.data
      }));
      
      const isRead = response.data.dias_lidos?.includes(dayNumber);
      if (isRead) {
        toast.success("Dia marcado como lido!");
      }
    } catch (error) {
      toast.error("Erro ao atualizar progresso");
    }
  };

  const goToPreviousDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1);
    }
  };

  const goToNextDay = () => {
    if (selectedPlan && currentDayIndex < selectedPlan.dias.length - 1) {
      setCurrentDayIndex(currentDayIndex + 1);
    }
  };

  const getProgressPercent = (plan) => {
    const planProgress = allProgress[plan.id] || { dias_lidos: [] };
    const totalDays = plan.dias?.length || 0;
    const readDays = planProgress.dias_lidos?.length || 0;
    return totalDays > 0 ? Math.round((readDays / totalDays) * 100) : 0;
  };

  const currentDay = selectedPlan?.dias?.[currentDayIndex];
  const isCurrentDayRead = currentDay && progress.dias_lidos?.includes(currentDay.dia);
  const totalDays = selectedPlan?.dias?.length || 0;
  const readDays = progress.dias_lidos?.length || 0;
  const progressPercent = totalDays > 0 ? (readDays / totalDays) * 100 : 0;

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground mt-2">Carregando devocionais...</p>
      </div>
    );
  }

  // No plans available
  if (plans.length === 0) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="py-12 text-center">
            <Book className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Nenhum Devocional Disponível</h2>
            <p className="text-muted-foreground">
              Não há planos de leitura cadastrados para sua unidade no momento.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show plan list if no plan is selected
  if (!selectedPlan) {
    return (
      <div className="space-y-4 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <BookOpen className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Devocionais</h1>
            <p className="text-muted-foreground text-sm">
              Escolha um plano de leitura para começar
            </p>
          </div>
        </div>

        {/* Plans List */}
        <div className="space-y-3">
          {plans.map((plan) => {
            const percent = getProgressPercent(plan);
            const planProgress = allProgress[plan.id] || { dias_lidos: [] };
            const daysRead = planProgress.dias_lidos?.length || 0;
            const totalPlanDays = plan.dias?.length || 0;
            
            return (
              <Card 
                key={plan.id}
                className="cursor-pointer hover:shadow-md transition-all hover:border-primary/50"
                onClick={() => selectPlan(plan)}
                data-testid={`plan-${plan.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{plan.nome}</h3>
                        {plan.ativo && (
                          <Badge variant="default" className="text-xs">Ativo</Badge>
                        )}
                      </div>
                      {plan.descricao && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {plan.descricao}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Badge variant="secondary" className="text-lg px-3 py-1">
                        {percent}%
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {totalPlanDays} dias
                      </span>
                      <span>{daysRead} de {totalPlanDays} lidos</span>
                    </div>
                    <Progress value={percent} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Show selected plan content
  if (!selectedPlan.dias || selectedPlan.dias.length === 0) {
    return (
      <div className="p-4">
        <Button 
          variant="ghost" 
          onClick={goBackToList}
          className="mb-4"
          data-testid="back-to-list"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para lista
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <Book className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Devocional Vazio</h2>
            <p className="text-muted-foreground">
              Este plano ainda não possui dias cadastrados.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={goBackToList}
        className="mb-2"
        data-testid="back-to-list"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar para lista
      </Button>

      {/* Modern Header with Logo */}
      <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <CardContent className="py-6">
          <div className="flex flex-col items-center mb-4">
            <ContemplationLogo className="mb-4" />
          </div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <h2 className="font-semibold text-white">{selectedPlan.nome}</h2>
                <p className="text-sm text-slate-400">
                  {readDays} de {totalDays} dias lidos
                </p>
              </div>
            </div>
            <Badge className="text-lg px-3 py-1 bg-orange-500/20 text-orange-300 border-orange-500/30">
              {Math.round(progressPercent)}%
            </Badge>
          </div>
          <Progress value={progressPercent} className="h-2 bg-slate-700" />
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between px-2">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPreviousDay}
          disabled={currentDayIndex === 0}
          data-testid="prev-day"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Anterior
        </Button>
        
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Dia</p>
          <p className="text-2xl font-bold">{currentDay?.dia || 1}</p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={goToNextDay}
          disabled={currentDayIndex >= totalDays - 1}
          data-testid="next-day"
        >
          Próximo
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Current Day Content - Modernized */}
      <Card className="overflow-hidden border shadow-md">
        <CardHeader className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant={isCurrentDayRead ? "default" : "outline"}
                size="sm"
                onClick={() => toggleDay(currentDay.dia)}
                className={`gap-2 ${isCurrentDayRead ? 'bg-green-600 hover:bg-green-700' : ''}`}
                data-testid="toggle-read"
              >
                {isCurrentDayRead ? (
                  <>
                    <CheckSquare className="h-4 w-4" />
                    Lido
                  </>
                ) : (
                  <>
                    <Square className="h-4 w-4" />
                    Marcar como Lido
                  </>
                )}
              </Button>
            </div>
            <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
              <Calendar className="h-3 w-3 mr-1" />
              {currentDay?.data}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6 space-y-6">
          {/* Reading Plan - Clickable to open Bible */}
          <button
            className="w-full p-4 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800/50 hover:shadow-md transition-all text-left cursor-pointer group"
            onClick={() => setBibleModalOpen(true)}
            data-testid="open-bible-btn"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Book className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <span className="font-semibold text-orange-700 dark:text-orange-300">Plano de Leitura</span>
              </div>
              <Badge variant="outline" className="text-orange-600 border-orange-300 dark:border-orange-700 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/50 transition-colors">
                <BookOpen className="h-3 w-3 mr-1" />
                Abrir Bíblia
              </Badge>
            </div>
            <p className="text-lg font-medium text-slate-800 dark:text-slate-200">{currentDay?.leitura}</p>
          </button>

          {/* Theme - Modernized */}
          {currentDay?.tema && (
            <div className="text-center py-6 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-900/10 dark:via-orange-900/10 dark:to-amber-900/10 rounded-xl border border-amber-200/50 dark:border-amber-800/30">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em]">Tema do Dia</span>
                <Sparkles className="h-4 w-4 text-amber-500" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white">{currentDay.tema}</h3>
            </div>
          )}

          {/* Verse - Modernized with justified text */}
          {currentDay?.versiculo && (
            <div className="p-5 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-l-4 border-orange-500 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide">Versículo Chave</span>
              </div>
              <p className="text-lg italic text-slate-700 dark:text-slate-300 leading-relaxed text-justify">
                "{currentDay.versiculo}"
              </p>
            </div>
          )}

          {/* Contemplation - Justified text */}
          {currentDay?.contemplacao && (
            <div className="space-y-3 p-4 rounded-xl bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/30">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-rose-100 dark:bg-rose-900/50 flex items-center justify-center">
                  <Heart className="h-4 w-4 text-rose-500" />
                </div>
                <span className="font-semibold text-rose-700 dark:text-rose-400">Contemplação</span>
              </div>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-justify pl-10">
                {currentDay.contemplacao}
              </p>
            </div>
          )}

          {/* Practice - Justified text */}
          {currentDay?.pratica && (
            <div className="space-y-3 p-4 rounded-xl bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                  <Target className="h-4 w-4 text-emerald-500" />
                </div>
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">Prática</span>
              </div>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-justify pl-10">
                {currentDay.pratica}
              </p>
            </div>
          )}

          {/* Declaration - Modernized with justified text */}
          {currentDay?.declaracao && (
            <div className="p-5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <span className="font-semibold text-white/90 uppercase tracking-wide text-sm">Declare em Voz Alta</span>
              </div>
              <p className="text-lg font-medium italic text-white leading-relaxed text-justify pl-10">
                "{currentDay.declaracao}"
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Day Selector (Mini Grid) */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">Visão Geral</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {selectedPlan.dias.map((day, index) => {
              const isRead = progress.dias_lidos?.includes(day.dia);
              const isCurrent = index === currentDayIndex;
              return (
                <button
                  key={day.dia}
                  onClick={() => setCurrentDayIndex(index)}
                  className={`
                    w-9 h-9 rounded-lg text-sm font-medium transition-all
                    ${isRead ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}
                    ${isCurrent ? 'ring-2 ring-primary ring-offset-2' : ''}
                  `}
                  data-testid={`day-${day.dia}`}
                >
                  {day.dia}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Bible Reader Modal */}
      <BibleReaderModal
        open={bibleModalOpen}
        onOpenChange={setBibleModalOpen}
        readingPlan={currentDay?.leitura}
        version={bibleVersion}
        onVersionChange={setBibleVersion}
      />
    </div>
  );
}
